import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  useFileLoader,
  useFileInput,
  useFullscreen,
} from "./FilePreviewer.hooks";
import { FilePreviewerProps, FileViewerHandle } from "./FilePreviewer.types";
import {
  CSS_CLASSES,
  DEFAULT_ACCEPTED_TYPES,
  ZOOM_CONFIG,
} from "./FilePreviewer.config";
import Toolbar from "./Toolbar/Toolbar";
import "./FilePreviewer.scss";
import FileViewerAdapter from "./FileViewerAdapter/FileViewerAdapter";

/**
 * A React component that provides file preview functionality with optional print, download, and fullscreen capabilities.
 * Supports various file types through the react-file-viewer library.
 *
 * @param {FilePreviewerProps} props - The properties for the FilePreviewer component
 */
const FilePreviewer: React.FC<FilePreviewerProps> = ({
  fileUrl,
  fileType,
  fileObject = null,
  title,
  enablePrint = true,
  enableDownload = true,
  enableFullscreen = true,
  enableZoom = true,
  onFileUpload,
  acceptedFileTypes = undefined, // Optional prop for accepted file types
}: FilePreviewerProps) => {
  /** Reference to the file viewer component for zoom and print functionality */
  const fileViewerRef = useRef<FileViewerHandle>(null);

  // File loading with axios-based hook to avoid react-file-viewer loading issues
  const {
    blobUrl,
    loading: fileLoading,
    error: fileError,
    retry,
    fileName,
  } = useFileLoader(fileUrl, fileType, fileObject);

  const [zoomLevel, setZoomLevel] = useState<number>(ZOOM_CONFIG.DEFAULT);
  const [isFullscreen, setIsFullscreen] = useFullscreen();

  // File input management hook
  const { fileInputRef, handleUploadClick, handleFileChange } = useFileInput(
    onFileUpload,
    acceptedFileTypes ?? undefined
  );

  /**
   * Renders the file viewer component with error handling
   * @returns JSX element containing the file viewer or error message
   */
  const renderViewer = () => {
    let viewerContent: React.ReactNode = null;

    if (fileLoading) {
      viewerContent = (
        <div style={{ padding: "20px", textAlign: "center" }}>
          Loading file...
        </div>
      );
    } else if (fileError) {
      viewerContent = (
        <div style={{ padding: "20px", color: "red", textAlign: "center" }}>
          <div>Failed to load file.</div>
          <button
            onClick={retry}
            style={{ marginTop: "10px", padding: "5px 10px" }}
          >
            Retry
          </button>
        </div>
      );
    } else if (blobUrl) {
      viewerContent = (
        <FileViewerAdapter
          key={blobUrl} // Force re-mount when blobUrl changes
          fileType={fileType}
          filePath={blobUrl}
          fileName={fileName}
          ref={fileViewerRef}
          onZoomChange={setZoomLevel}
        />
      );
    }

    return <div className={CSS_CLASSES.VIEWER}>{viewerContent}</div>;
  };
  if (isFullscreen) {
    return createPortal(
      <div className={CSS_CLASSES.FULLSCREEN_OVERLAY}>
        <div className={CSS_CLASSES.FULLSCREEN_CONTAINER}>
          {" "}
          {onFileUpload && (
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileChange}
              style={{ display: "none" }}
              accept={acceptedFileTypes ?? DEFAULT_ACCEPTED_TYPES}
            />
          )}
          <Toolbar
            title={title}
            enableZoom={enableZoom}
            enableUpload={!!onFileUpload}
            enableDownload={enableDownload}
            enablePrint={enablePrint}
            enableFullscreen={enableFullscreen}
            fileLoading={fileLoading}
            fileError={fileError}
            blobUrl={blobUrl}
            fileUrl={fileUrl}
            zoomLevel={zoomLevel}
            isFullscreen={isFullscreen}
            onZoomIn={() => fileViewerRef.current?.zoomIn()}
            onZoomOut={() => fileViewerRef.current?.zoomOut()}
            onUploadClick={handleUploadClick}
            onDownload={() => fileViewerRef.current?.download()}
            onPrint={() => fileViewerRef.current?.print()}
            onToggleFullscreen={setIsFullscreen}
          />
          {renderViewer()}
        </div>
      </div>,
      document.body
    );
  }
  return (
    <div className={CSS_CLASSES.CONTAINER}>
      {" "}
      {onFileUpload && (
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileChange}
          style={{ display: "none" }}
          accept={acceptedFileTypes ?? DEFAULT_ACCEPTED_TYPES}
        />
      )}
      <Toolbar
        title={title}
        enableZoom={enableZoom}
        enableUpload={!!onFileUpload}
        enableDownload={enableDownload}
        enablePrint={enablePrint}
        enableFullscreen={enableFullscreen}
        fileLoading={fileLoading}
        fileError={fileError}
        blobUrl={blobUrl}
        fileUrl={fileUrl}
        zoomLevel={zoomLevel}
        isFullscreen={isFullscreen}
        onZoomIn={() => fileViewerRef.current?.zoomIn()}
        onZoomOut={() => fileViewerRef.current?.zoomOut()}
        onUploadClick={handleUploadClick}
        onDownload={() => fileViewerRef.current?.download()}
        onPrint={() => fileViewerRef.current?.print()}
        onToggleFullscreen={setIsFullscreen}
      />
      {renderViewer()}
    </div>
  );
};

export default FilePreviewer;
